/**
 * Static label tree. Retained as the TYPE ANCHOR for `TpoGenConfig['labels']`;
 * the runtime values come from `fe_config.labels` via `feConfigAdapter`.
 */
export const LABELS = {
    common: {
        breadcrumbRoot: 'Trade Promo Optimization',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        close: 'Close',
        actions: 'Actions',
        noData: 'No Data',
    },
    planIndex: {
        sectionTitle: 'Promotion Planner',
        description: 'Build and manage promotion plans by customer, category, and year',
        breadcrumb: 'Planner',
        createNewPlan: 'Create New Plan',
        myPlan: 'My Plan',
        fields: 'Fields',
        selectFields: 'Select Fields',
        tabs: {
            plans: 'Plans',
            comparisonReports: 'Comparison Reports',
        },
        table: {
            planningGroups: 'Planning Groups',
            createdBy: 'Created by',
            status: 'Status',
            lastUpdated: 'Last Updated',
            dateCreated: 'Date Created',
            planVersion: 'Plan Version',
            plans: 'Plans',
            createNewPlan: 'Create new plan',
            collapse: 'Collapse',
            expand: 'Expand',
            editPlan: 'Edit plan',
        },
    },
    planDetail: {
        breadcrumb: 'Planner',
        customer: 'Customer: ',
        category: 'Category: ',
        year: 'Year: ',
        createNewPlan: 'Create New Plan',
        exitWithoutPromoSave: 'Kindly save your changes to Proceed, cant proceed without adding a promo',
    },
    events: {
        addNewEvent: 'Add New Event',
        editEvent: 'Edit Event',
        eventDetails: 'Event Details',
        productPortfolio: 'Product Portfolio',
        addEventDetails: 'Add Event Details',
        editEventDetails: 'Edit Event Details',
        addPromoDetails: 'Add Promo Details',
        editPromoDetails: 'Edit Promo Details',
        update: 'Update',
    },
    promos: {
        sectionTitle: 'Promotions',
        addPromo: 'Add Promo',
        editPromo: 'Edit Promo',
        noPromotionsAdded: 'No Promotions Added.',
        addPromoTab: '+ Add Promo',
    },
    createPlanModal: {
        titleCreate: 'Create New Plan',
        titleEdit: 'Edit Plan',
        okCreate: 'Create Plan',
        form: {
            planName: 'Plan Name',
            planNamePlaceholder: 'Enter Plan Name',
            planNameRequired: 'Plan Name is required',
            productCategory: 'Product Category',
            productCategoryPlaceholder: 'Select Product Category',
            productCategoryRequired: 'Product Category is required',
            customer: 'Customer',
            customerPlaceholder: 'Select Customer',
            customerRequired: 'Customer is required',
            year: 'Year',
            yearPlaceholder: 'Select Year',
            yearRequired: 'Year is required',
            planVersion: 'Plan Version',
            planVersionPlaceholder: 'Select Plan Version',
            planVersionRequired: 'Plan Version is required',
        },
    },
    modals: {
        unsavedPromoChanges: 'Unsaved Promo Changes',
        unsavedEventChanges: 'Unsaved Event Changes',
        unsavedPromoDescription: 'You have unsaved changes to this promo. If you leave now, your changes will be lost.',
        unsavedEventDescription: 'You have unsaved changes to this event. If you leave now, your changes will be lost.',
        continueEditing: 'Continue Editing',
        discardChanges: 'Discard Changes',
        deletePromoSubTitle: 'This will delete the promotion from the event.',
        noPromotionsTitle: 'No Promotions Added',
        deleteEvent: 'Delete Event',
        noPromotionsDescription:
            'No promotions have been created for this event yet. If you proceed, the event will also be permanently deleted.',
    },
    optimizePlan: {
        toolbarButton: 'Optimize Plan',
        modalTitle: 'Optimise Plan',
        tabs: {
            scope: 'Scope',
            constraints: 'Constraints',
        },
        runOptimisation: 'Run Optimisation',
        scopeSection: 'Scope of Optimisation',
        baseInputsSection: 'Base Inputs',
        loadingPlaceholder: 'Loading...',
        constraintsSection: 'Constraints',
        levelsLabel: 'Levels:',
        selectLevelPlaceholder: 'Select Level',
        tableError: 'An unexpected error occurred. Please try again later.',
        emptyConstraints: 'Select objective, dimension and start date',
        selector: {
            buttonLabel: 'Select Constraints',
            searchPlaceholder: 'Search KPI...',
            selectAll: 'Select All',
            unselectAll: 'Unselect All',
            noResults: 'No matching constraints',
            cancel: 'Cancel',
            apply: 'Apply',
        },
        notifications: {
            optimizationFailed: 'Optimization failed. Please try again.',
            infeasibleToast: 'No feasible solution found for this plan.',
            infeasibleTagLabel: 'No solution found',
            infeasibleTooltipFallback: 'No feasible solution found for this plan.',
        },
    },
} as const;

export type Labels = typeof LABELS;
